import * as React from 'react'
import { useState, useRef } from 'react'
import { useTranslation } from 'hooks'
import * as S from './ConsignmentRetrieval.styles'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

const UploadIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const FileIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
)

interface UploadStepProps {
  file: File | null
  onFileSelect: (file: File | null) => void
}

const UploadStep: React.FC<UploadStepProps> = ({ file, onFileSelect }) => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateAndSet = (f: File) => {
    setError(null)

    if (f.type !== 'application/pdf') {
      setError(t('views.account.consignment_retrieval.upload_invalid_type'))
      return
    }

    if (f.size > MAX_FILE_SIZE) {
      setError(t('views.account.consignment_retrieval.upload_too_large'))
      return
    }

    onFileSelect(f)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped) validateAndSet(dropped)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) validateAndSet(selected)
  }

  const handleReplace = () => {
    onFileSelect(null)
    setError(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <>
      {!file ? (
        <S.DropZone
          isDragging={isDragging}
          hasError={!!error}
          htmlFor="label-pdf-upload"
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <S.DropZoneIcon>
            <UploadIcon />
          </S.DropZoneIcon>
          <S.DropZoneText>
            {t('views.account.consignment_retrieval.upload_label')}
          </S.DropZoneText>
          <S.DropZoneHint>
            {t('views.account.consignment_retrieval.upload_hint')}
          </S.DropZoneHint>
          <S.HiddenInput
            ref={inputRef}
            id="label-pdf-upload"
            type="file"
            accept="application/pdf"
            onChange={handleChange}
          />
        </S.DropZone>
      ) : (
        <S.FileRow>
          <S.FileName>
            <FileIcon /> {file.name}
          </S.FileName>
          <S.ReplaceLink type="button" onClick={handleReplace}>
            {t('views.account.consignment_retrieval.replace_label')}
          </S.ReplaceLink>
        </S.FileRow>
      )}
      {error && <S.ErrorText>{error}</S.ErrorText>}
    </>
  )
}

export default UploadStep
